import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { statusInk } from '@/lib/status';
import { markOrdered, type OrderSummary } from '@/api/orders';
import { RowAction } from '../workLib';
import { todayDateString } from '../workUtils';

/** The journey header's Mark ordered form: placed date + optional order
 *  number, inline. The order number matters — stored as a ref, it lets the
 *  incoming confirmation auto-attach instead of relying on the fuzzy match. */
export function MarkOrderedForm({ order, onDone, onCancel }: {
  order: OrderSummary;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [placedDate, setPlacedDate] = useState(todayDateString());
  const [orderNumber, setOrderNumber] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div
      className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/40 px-2.5 py-2"
      onClick={(e) => e.stopPropagation()}
    >
      <span className="text-xs font-medium">Placed</span>
      <Input aria-label="Placed date" type="date" value={placedDate} onChange={(e) => setPlacedDate(e.target.value)} className="h-7 w-36 px-1.5 text-xs" />
      <Input
        value={orderNumber}
        onChange={(e) => setOrderNumber(e.target.value)}
        aria-label="Order or purchase order number" placeholder="Order / PO # (optional)"
        className="h-7 w-44 px-1.5 text-xs"
      />
      <Button
        size="sm"
        className="text-xs"
        disabled={busy || !placedDate}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            await markOrdered(order.id, { placedDate, orderNumber: orderNumber.trim() || null });
            onDone();
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to mark ordered');
            setBusy(false);
          }
        }}
      >
        {busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
        Mark ordered
      </Button>
      <RowAction variant="quiet" onClick={onCancel} disabled={busy}>
        Cancel
      </RowAction>
      {error && <span className={`text-xs ${statusInk('danger')}`}>{error}</span>}
    </div>
  );
}
